import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {isReadonly, statusTypes} from 'ag-common/tools/fileStatus'
import actions from 'redux-auto'
import i18n from 'i18next'
import {getWrongAnswers} from 'ag-common/tools/questionValidator'
import {findNestedValue} from 'ag-common/tools/objects'
import {isNotDraft, isSubmitted, isRejected, isConfirmed} from 'ag-common/tools/ausbildungsplan/chapterStatus'
import ControllableTabs from '#components/m/ControllableTabs'
import ErrorModal from '#components/m/ErrorModal'
import AnswerComment from './AnswerComment'
import Question from './Question'
import AusbildungsplanHeader from './AusbildungsplanHeader'

const FIRST_TAB_INDEX = 0

export class QuestionSet extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedTab: 0,
      hasUnsavedChanges: false,
      wrongAnswerIds: [],
    }
  }

  componentDidMount() {
    this.getAnswers()
    this.getChapterStatus()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.azubiId !== this.props.azubiId || prevProps.chapterId !== this.props.chapterId) {
      this.getAnswers()
    }
    if (prevProps.chapterId !== this.props.chapterId) {
      this.getChapterStatus()
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        selectedTab: FIRST_TAB_INDEX,
      })
    }
  }

  handleSaveComment(e, questionId) {
    const {azubiId, chapterId, postComment, currentUserId} = this.props
    const text = e.target.value
    clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => {
      postComment({
        azubiId,
        kapitelId: chapterId,
        frageId: questionId,
        text,
        authorId: currentUserId,
      })
    }, 500)
  }

  getAnswers = () => {
    const {azubiId, permissions} = this.props
    if (permissions.ausbildungsplan.viewAusbildungsplanFromOthers) {
      this.props.getAnswersAsAusbilder(azubiId)
    } else {
      this.props.getAnswers()
    }
  }

  getChapterStatus = () => {
    const {chapterId, azubiId, permissions} = this.props
    if (permissions.ausbildungsplan.viewAusbildungsplanFromOthers) {
      this.props.getChapterStatusAsAusbilder(chapterId, azubiId)
    } else {
      this.props.getChapterStatus(chapterId)
    }
  }

  setUnsavedChangesState = hasUnsavedChanges => {
    this.setState({hasUnsavedChanges})
  }

  isChapterReadonly = () =>
    this.props.permissions.ausbildungsplan.viewAusbildungsplanFromOthers || isReadonly(this.props.chapterStatus)

  onSelectTab = async n => {
    this.setState({selectedTab: n})
  }

  onNextQuestion = () => {
    this.setState(prevState => ({
      selectedTab: Math.min(prevState.selectedTab + 1, this.props.questions.length - 1),
    }))
  }

  tryValidateAnswers = () => {
    const {questions, chapterAnswers, getWrongAnswersFromValidator} = this.props
    const wrongAnswerIds = getWrongAnswersFromValidator(questions, chapterAnswers)
    if (wrongAnswerIds.length > 0) {
      this.setState({
        showValidationError: true,
        validationErrorMessage: this.getValidationErrorMessage(questions, wrongAnswerIds),
        wrongAnswerIds,
      })
      return false
    }
    this.setState({
      showValidationError: false,
      validationErrorMessage: null,
      wrongAnswerIds: [],
    })
    return true
  }

  getValidationErrorMessage = (questions, wrongAnswerIds) => {
    const wrongAufgabenNummer = questions
      .filter(x => wrongAnswerIds.includes(x.id))
      .map(x => x.order)
      .join(', ')
    return wrongAnswerIds.length === 1
      ? i18n.t('ausbildungsplan_validation_error_message_singular', [wrongAufgabenNummer])
      : i18n.t('ausbildungsplan_validation_error_message_plural', [wrongAufgabenNummer])
  }

  hasComment = () => {
    const {questions, chapterAnswers} = this.props
    return questions.some(
      q => chapterAnswers[q.id] && chapterAnswers[q.id].kommentar && chapterAnswers[q.id].kommentar.text !== '',
    )
  }

  getTabContent = hasComment => {
    const {permissions, questions, chapterId, azubiId, chapterAnswers, chapterStatus, currentUserId} = this.props
    const {wrongAnswerIds} = this.state
    return questions.map(q => {
      const content = [
        q.order,
        <Question
          key={q.id}
          azubiId={azubiId}
          chapterId={chapterId}
          question={q}
          isReadOnly={this.isChapterReadonly()}
          setHasUnsavedChanges={this.setUnsavedChangesState}
          isWrong={wrongAnswerIds.includes(q.id)}
        />,
      ]
      if (isSubmitted(chapterStatus) || isRejected(chapterStatus) || (isConfirmed(chapterStatus) && hasComment)) {
        content.push(
          <AnswerComment
            canEdit={permissions.ausbildungsplan.comment.edit}
            canView={permissions.ausbildungsplan.comment.view}
            key={q.id}
            id={q.id}
            kommentar={chapterAnswers[q.id] ? chapterAnswers[q.id].kommentar : undefined}
            chapterStatus={chapterStatus}
            onChangeHandle={e => this.handleSaveComment(e, q.id)}
            currentUserId={currentUserId}
          />,
        )
      }
      return content
    })
  }

  render() {
    const {questions, title, chapterId, azubiId, chapterStatus, permissions} = this.props
    const {selectedTab, hasUnsavedChanges} = this.state

    const isLastQuestion = this.state.selectedTab + 1 === this.props.questions.length

    const hasComment = this.hasComment()
    const tabContent = this.getTabContent(hasComment)

    return (
      <>
        <AusbildungsplanHeader
          title={title}
          azubiId={azubiId}
          chapterId={chapterId}
          chapterStatus={chapterStatus}
          nextButtonClicked={this.onNextQuestion}
          lastQuestion={isLastQuestion}
          questionsLength={questions.length}
          hasChanges={hasUnsavedChanges}
          hasComment={hasComment}
          tryValidateAnswers={this.tryValidateAnswers}
        />
        <div
          className={`ausbildungsplan__content-wrapper ${
            isNotDraft(chapterStatus) && 'ausbildungsplan__content-wrapper--wide'
          }`}
        >
          <div className="ausbildungsplan__content">
            <ControllableTabs
              chapterId={chapterId}
              type="numbers"
              tabz={tabContent}
              onSelectTab={this.onSelectTab}
              selectedTab={selectedTab}
              hasComment={hasComment}
              canViewComment={permissions.ausbildungsplan.comment.view}
            />
          </div>
        </div>
        <ErrorModal
          modifier="error-sign"
          overlayClassName="ausbildungsplan__content-modal"
          onButtonClick={() => this.setState({showValidationError: false})}
          showModal={this.state.showValidationError}
          headline={i18n.t('ausbildungsplan_validation_error_title')}
          text={this.state.validationErrorMessage}
          buttonText={i18n.t('ausbildungsplan_validation_error_button')}
        />
      </>
    )
  }
}

const mapStateToProps = (state, props) => ({
  permissions: state.authentication.permissions,
  currentUserId: state.authentication.id,
  chapterStatus:
    findNestedValue(state, ['ausbildungsplan', 'chapterStatuses', props.azubiId, props.chapterId, 'status']) ||
    statusTypes.draft,
  chapterAnswers: findNestedValue(state, ['ausbildungsplan', 'answers', props.chapterId]),
})

function mapDispatchToProps() {
  return {
    getAnswers: () => actions.ausbildungsplan.getAnswers(),
    getAnswersAsAusbilder: azubiId => actions.ausbildungsplan.getAnswersAsAusbilder({azubiId}),
    getChapterStatus: chapterId => actions.ausbildungsplan.getChapterStatus({chapterId}),
    getChapterStatusAsAusbilder: (chapterId, azubiId) =>
      actions.ausbildungsplan.getChapterStatusAsAusbilder({chapterId, azubiId}),
    postComment: payload => actions.ausbildungsplan.postComment(payload),
  }
}

QuestionSet.defaultProps = {
  chapterAnswers: {},
  getWrongAnswersFromValidator: getWrongAnswers,
}

QuestionSet.propTypes = {
  permissions: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  chapterId: PropTypes.string.isRequired,
  azubiId: PropTypes.number,
  currentUserId: PropTypes.number.isRequired,
  getAnswers: PropTypes.func.isRequired,
  getAnswersAsAusbilder: PropTypes.func.isRequired,
  getChapterStatus: PropTypes.func.isRequired,
  getChapterStatusAsAusbilder: PropTypes.func.isRequired,
  chapterAnswers: PropTypes.object,
  chapterStatus: PropTypes.string,
  postComment: PropTypes.func.isRequired,
  getWrongAnswersFromValidator: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(QuestionSet)
